<template>
  <div class="perwakilan-dinas-table">
    <label class="section-label">Perwakilan Dinas <span class="required">*</span></label>
    <div class="table-wrapper" :class="{ 'has-error': error }">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :pagination="false"
        size="small"
        class="table-component"
        row-key="id"
      >
        <template #namaPengurus="{ record }">
          <a-auto-complete
            v-model:value="record.searchValue"
            :placeholder="record.isNonPengurus ? 'Non-Pengurus (dapat diedit)' : 'Cari atau tambah non-pengurus...'"
            :options="getFilteredOptions(record.searchValue)"
            style="width: 100%"
            :disabled="disabled"
            @select="(value, option) => handleSelect(value, option, record)"
            @change="() => handleSearchChange(record)"
          >
            <template #option="option">
              <div v-if="option.isNew" class="add-non-pengurus-option">
                <PlusOutlined style="margin-right: 8px" />
                <span style="color: #1890ff; font-weight: 500">{{ option.label }}</span>
              </div>

              <div v-else>{{ option.label }}</div>
            </template>
          </a-auto-complete>
          <a-badge
            v-if="record.isNonPengurus && record.searchValue"
            status="warning"
            text="Non-Pengurus"
            style="margin-top: 4px; font-size: 12px;"
          />
        </template>
        <template #jabatan="{ record }">
          <a-input
            v-if="record.isNonPengurus"
            v-model:value="record.jabatanNonPengurus"
            placeholder="Jabatan"
            :disabled="disabled"
            @change="handleChange"
            @blur="handleChange"
          />
          <span v-else>{{ getJabatan(record.userId) }}</span>
        </template>
        <template #npaPerki="{ record }">
          <a-input
            v-if="record.isNonPengurus"
            v-model:value="record.npaNonPengurus"
            placeholder="NPA"
            :disabled="disabled"
            @change="handleChange"
            @blur="handleChange"
          />
          <span v-else>{{ getNpa(record.userId) }}</span>
        </template>
        <template #aksi="{ index, record }">
          <div class="action-cell">
            <button
              type="button"
              class="delete-button"
              @click.prevent="handleRemove(index)"
              title="Hapus file"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>

            <!-- Preview button (for form preview, emits event to parent) -->
            <a-tooltip title="Preview Surat Tugas">
              <button
                type="button"
                class="preview-button"
                @click.prevent="handlePreview(index, record)"
                title="Preview"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
            </a-tooltip>
          </div>
        </template>
      </a-table>
    </div>
    <span v-if="error" class="error-message">{{ error }}</span>
    <a-button v-if="!disabled" type="link" class="btn-tambah-baris" @click="handleAdd">
      <PlusOutlined /> TAMBAH BARIS
    </a-button>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfModalVisible"
      :title="pdfModalTitle"
      :pdf-path="currentPdfPath"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { DeleteOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons-vue'
import PDFPreviewModal from './PDFPreviewModal.vue'

export default defineComponent({
  name: 'PerwakilanDinasTable',
  components: {
    PlusOutlined,
    PDFPreviewModal,
  },
  props: {
    dataSource: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showPdfButton: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: '',
    },
  },
  emits: ['add', 'remove', 'change', 'view-pdf', 'preview'],
  setup(props, { emit }) {
    const store = useStore()

    // PDF Modal state
    const pdfModalVisible = ref(false)
    const pdfModalTitle = ref('Preview Surat Keluar')
    const currentPdfPath = ref('')

    // Flag to prevent @change from interfering with @select
    const justSelected = ref(false)

    // Filter out admin and super_admin roles - they are not typically perwakilan dinas
    const pengurusOptions = computed(() => {
      const options = store.state.suratKeluar.pengurusOptions || []
      const pengurusMap = store.state.suratKeluar.pengurusMap || {}
      return options.filter(option => {
        const user = pengurusMap[option.value]
        if (user && user.role) {
          const role = user.role.toLowerCase()
          return role !== 'admin' && role !== 'super_admin' && role !== 'pekerja_harian'
        }
        return true
      })
    })

    // Get filtered options with "add non-pengurus" option
    const getFilteredOptions = (searchValue) => {
      if (!searchValue || searchValue.trim() === '') {
        // Mode Default: Tampilkan list pengurus
        return pengurusOptions.value.map(opt => ({
          value: opt.label,    // TAMPILAN: Nama (agar input box berisi nama)
          label: opt.label,    // LABEL: Nama
          userKey: opt.value,  // LOGIC: ID disimpan disini
          isNew: false,         // Penanda ini Pengurus,
        }))
      }

      const lowerSearch = searchValue.toLowerCase()
      // 1. Filter Pengurus
      const filteredRaw = pengurusOptions.value.filter(opt =>
        opt.label.toLowerCase().includes(lowerSearch),
      )

      const filtered = filteredRaw.map(opt => ({
          value: opt.label,
          label: opt.label,
          userKey: opt.value,
          isNew: false,
      }))

      // 2. Opsi "Tambah Non-Pengurus"
      // Hanya muncul jika tidak ada hasil exact match, atau user mengetik > 2 huruf
      // Kita cek apakah input user PERSIS sama dengan salah satu pengurus yg ada
      const isExactMatch = filtered.some(f => f.value.toLowerCase() === lowerSearch)

      if ((filtered.length === 0 || searchValue.length > 2) && !isExactMatch) {
        return [
          ...filtered,
          {
            // PERUBAHAN UTAMA DISINI:
            value: searchValue, // TAMPILAN: Gunakan teks input user (misal: "test")
            label: `Masukkan "${searchValue}" sebagai Non-Pengurus`, // LABEL: Teks instruksi
            isNew: true, // LOGIC: Flag penanda ini user baru,
            userKey: null,
          },
        ]
      }

      return filtered
    }

    const columns = computed(() => [
      {
        title: 'Nama',
        dataIndex: 'namaPengurus',
        key: 'namaPengurus',
        width: '35%',
        slots: { customRender: 'namaPengurus' },
      },
      {
        title: 'Jabatan',
        dataIndex: 'jabatan',
        key: 'jabatan',
        width: '30%',
        slots: { customRender: 'jabatan' },
      },
      {
        title: 'NPA PERKI',
        dataIndex: 'npaPerki',
        key: 'npaPerki',
        width: '30%',
        slots: { customRender: 'npaPerki' },
      },
      {
        title: 'Aksi',
        key: 'aksi',
        width: '5%',
        slots: { customRender: 'aksi' },
      },
    ])

    const getJabatan = userId => {
      return store.getters['suratKeluar/getJabatanByUserId'](userId)
    }

    const getNpa = userId => {
      return store.getters['suratKeluar/getNpaByUserId'](userId)
    }

    const handleAdd = () => {
      emit('add')
    }

    const handleRemove = index => {
      emit('remove', index)
    }

    const handleChange = () => {
      emit('change')
    }

  const handleSelect = (value, option, record) => {
    justSelected.value = true

    // Cek properti 'isNew' yang kita buat di getFilteredOptions
    if (option.isNew) {
      // --- LOGIC NON-PENGURUS ---
      // value otomatis sudah berisi nama bersih (misal: "test") karena kita set di atas
      record.isNonPengurus = true
      record.userId = null
      record.namaNonPengurus = value // Simpan nama
      record.jabatanNonPengurus = ''
      record.npaNonPengurus = ''
      record.searchValue = value

    } else {
      // --- LOGIC PENGURUS ---
      // Ambil ID dari userKey
      record.userId = option.userKey

      record.isNonPengurus = false
      record.namaNonPengurus = null
      record.jabatanNonPengurus = null
      record.npaNonPengurus = null
      record.searchValue = value // value adalah Nama Pengurus
    }

    setTimeout(() => {
      justSelected.value = false
    }, 100)

    handleChange()
  }

  const handleSearchChange = (record) => {
  if (justSelected.value) return

  if (record.searchValue) {
    if (record.isNonPengurus) {
       // Karena value sudah bersih, kita tinggal assign saja
       record.namaNonPengurus = record.searchValue
    } else {
      // Logic Pengurus: Pastikan nama yang diketik ada di list
      const matchesPengurus = pengurusOptions.value.some(opt => opt.label === record.searchValue)

      if (!matchesPengurus) {
        // Jika user mengedit nama pengurus jadi ngaco -> reset ID
        record.userId = null
      } else {
        // Jika user mengetik manual nama pengurus yg valid -> restore ID
        const match = pengurusOptions.value.find(opt => opt.label === record.searchValue)
        if (match) record.userId = match.value
      }
    }
  }
}
    const handleViewPdf = record => {
      if (record.pdfPath) {
        // Get the pengurus name for the title
        const pengurus = pengurusOptions.value.find(p => p.value === record.userId)
        const name = pengurus?.label || record.namaNonPengurus || 'Perwakilan'
        pdfModalTitle.value = `Surat Tugas - ${name}`
        currentPdfPath.value = record.pdfPath
        pdfModalVisible.value = true
      }
      emit('view-pdf', record)
    }

    const handlePreview = (index, record) => {
      emit('preview', { index, record })
    }

    return {
      columns,
      pengurusOptions,
      getFilteredOptions,
      getJabatan,
      getNpa,
      handleAdd,
      handleRemove,
      handleChange,
      handleSelect,
      handleSearchChange,
      handleViewPdf,
      pdfModalVisible,
      pdfModalTitle,
      currentPdfPath,
      handlePreview,
    }
  },
})
</script>

<style lang="scss" scoped>
.perwakilan-dinas-table {
  margin-bottom: 24px;

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-bottom: 8px;
    border-radius: 4px;
    transition: border-color 0.3s;

    &.has-error {
      border: 1px solid #ff4d4f;
    }

    @media (max-width: 768px) {
      overflow-x: auto;
    }
  }

  .error-message {
    display: block;
    color: #ff4d4f;
    font-size: 14px;
    margin-top: 4px;
    margin-bottom: 8px;
  }

  .section-label {
    display: block;
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    font-size: 14px;

    .required {
      color: #ff4d4f;
    }
  }

  .table-component {
    min-width: 800px;

    @media (max-width: 768px) {
      min-width: 1000px;
    }

    :deep(.ant-table-thead > tr > th) {
      background: #fafafa;
      font-weight: 600;
      font-size: 12px;
      color: #8c8c8c;
    }
  }

  .action-cell {
    display: flex;
    gap: 8px;

    .btn-view-pdf {
      background: #1890ff;
      border-color: #1890ff;
    }
  }

  .btn-tambah-baris {
    color: #262626;
    font-weight: 700;
    margin-bottom: 16px;
    font-size: 14px;
    padding: 0;
    height: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .delete-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50px;
    transition: all 0.2s;
    color: #262626; // Black by default

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: #f5f5f5;
      color: #ff4d4f; // Red on hover
    }
  }

  .preview-button {
    background: #ff4d4f;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50px;
    transition: all 0.2s;
    color: #ffffff;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: #e6f7ff;
      color: #262626;
    }
  }

  .non-pengurus-display {
    display: flex;
    align-items: center;
    padding: 4px 0;

    :deep(.ant-badge-status-dot) {
      width: 8px;
      height: 8px;
    }

    :deep(.ant-badge-status-text) {
      font-weight: 500;
      color: #262626;
    }
  }

  .add-non-pengurus-option {
    display: flex;
    align-items: center;
    padding: 4px 0;
  }

  :deep(.ant-input) {
    font-size: 14px;
  }

  :deep(.ant-auto-complete) {
    .ant-select-selector {
      border-radius: 4px;
    }
  }
}
</style>
